import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { type CreateScreenSchema, screenSchemas } from '@/api/screens/screen.schema';
import { Button } from '@/components/ui/button';
import { Content } from '@/components/ui/content';
import { Form, FormContent, FormFooter } from '@/components/ui/form';
import { FormInput } from '@/components/ui/form-fields/form-input';
import { FormMultiSelect } from '@/components/ui/form-fields/form-multi-select';
import { FormSwitch } from '@/components/ui/form-fields/form-switch';
import { Icon } from '@/components/ui/icon';
import { useRouter } from '@/hooks/use-router';
import { meta } from '@/lib/meta';
import { useCreateScreen } from '@/services/screens';
import type { ValidationError } from '@/types/api/validation-error';
import { LayoutEditor } from './+components/layout-editor';
import { ScreenPreview } from './+components/preview';

export const Route = createFileRoute('/_app/screens/create')({
  head: () => meta({ label }),
  component: RouteComponent,
});

type Schema = CreateScreenSchema;
const schema = screenSchemas.create;
const model = 'screens';
const label = 'Nova Tela';

const ACTION_OPTIONS = [
  { label: 'Listar (browse)', value: 'browse' },
  { label: 'Criar (create)', value: 'create' },
  { label: 'Visualizar (read)', value: 'read' },
  { label: 'Editar (update)', value: 'update' },
  { label: 'Excluir (delete)', value: 'delete' },
];

function RouteComponent() {
  const router = useRouter();
  const { mutate: save, error, isSuccess, isPending: isSubmitting } = useCreateScreen();

  const form = useForm<Schema>({
    resolver: zodResolver(schema),
    shouldUseNativeValidation: false,
    defaultValues: {
      showInMenu: true,
      isActive: true,
      actions: ['browse', 'create', 'read', 'update', 'delete'],
      layout: { columns: [], filters: [], fields: [] },
    } as any,
  });

  const handleSubmit = (data: Schema) => save(data);

  useEffect(() => {
    if (isSuccess) router.back();
  }, [isSuccess, router]);

  useEffect(() => {
    if (error && error.status === 422) {
      const { errors } = error.value as unknown as ValidationError<Schema>;

      if (errors) {
        Object.entries(errors).forEach(([key, message]) => {
          form.setError(key as keyof Schema, { message });
        });
      }
    }
  }, [error, form]);

  return (
    <Content
      breadcrumbs={{
        title: label,
        items: [
          { label: 'Listagem', route: `/${model}` },
          { label: 'Nova', active: true },
        ],
      }}
    >
      <Form
        id="form"
        title="Dados da tela"
        description="Configure o recurso, o menu e o layout de listagem/formulário."
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormContent>
          <FormInput
            name="resource"
            control={form.control}
            label="Recurso (slug)"
            required
          />
          <FormInput
            name="label"
            control={form.control}
            label="Label (singular)"
            required
          />
          <FormInput
            name="pluralLabel"
            control={form.control}
            label="Label (plural)"
            required
          />
          <FormInput name="icon" control={form.control} label="Ícone (lucide)" />
          <FormInput
            name="menuOrder"
            type="number"
            control={form.control}
            label="Ordem no menu"
          />
          <FormInput name="menuGroup" control={form.control} label="Grupo no menu" />
          <FormSwitch name="showInMenu" control={form.control} label="Mostrar no menu" />
          <FormSwitch name="isActive" control={form.control} label="Ativa" />
          <FormMultiSelect
            name="actions"
            control={form.control}
            label="Ações habilitadas"
            options={ACTION_OPTIONS}
            sm={12}
            md={12}
            lg={12}
          />

          <LayoutEditor control={form.control as any} />
          <ScreenPreview control={form.control as any} />
        </FormContent>
        <FormFooter>
          <Button
            type="button"
            disabled={isSubmitting}
            variant="outline"
            onClick={() => form.reset()}
          >
            Limpar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="success"
            form="form"
            className="flex items-center gap-2"
          >
            <Icon name="save" />
            Salvar
          </Button>
        </FormFooter>
      </Form>
    </Content>
  );
}
